import { JSX, MouseEvent, useCallback, useEffect, useState } from 'react';
import { Confirm, Title, useAuthenticated, useNotify, usePermissions } from 'react-admin';
import { Alert, Box, Button, Chip, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import { permissionManager } from '../permissions/manager';
import {
    MAINTENANCE_CHANGED,
    MAINTENANCE_PERMISSION,
    MaintenancePhase,
    MaintenanceWindow,
    bannerFor,
    endMaintenance,
    formatWindowTime,
    isoToLocalInput,
    loadMaintenance,
    localInputToIso,
    scheduleMaintenance,
} from './maintenanceApi';

/** HQ-API refuses a longer message; kept equal to MaintenanceState::MESSAGE_MAX_LENGTH. */
const MESSAGE_MAX_LENGTH: number = 500;

const PHASE_LABELS: Record<MaintenancePhase, string> = {
    off: 'No window',
    scheduled: 'Scheduled',
    winding_down: 'Winding down',
    active: 'In progress',
};

type Confirming = 'start' | 'end' | null;

/**
 * Take focus off the button that opens a confirmation before the dialog opens. MUI hides the page
 * behind a dialog from assistive technology, and a button left focused underneath it is one a
 * screen reader user is still on while it is hidden (Chrome logs "Blocked aria-hidden").
 */
const blurTrigger = (event: MouseEvent<HTMLButtonElement>): void => event.currentTarget.blur();

const Detail = ({ label, value }: { label: string, value: string }): JSX.Element => (
    <Stack direction="row" spacing={ 2 }>
        <Typography variant="body2" color="text.secondary" sx={ { minWidth: 140 } }>{ label }</Typography>
        <Typography variant="body2">{ value === '' ? 'Not set' : value }</Typography>
    </Stack>
);

/**
 * Schedule, start and end maintenance windows (Stage 5 of Deployment-Resilience-Plan.md).
 *
 * Every signed-in admin can open it and see the window. The controls are for admins holding
 * `HQ_System::maintenance`, which HQ-API enforces whatever this screen shows.
 */
export const MaintenancePage = (): JSX.Element => {
    useAuthenticated();
    const notify = useNotify();
    const { permissions } = usePermissions();
    const canManage: boolean = permissionManager.isAllowed(permissions, MAINTENANCE_PERMISSION);

    const [maintenance, setMaintenance] = useState<MaintenanceWindow | null>(null);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);
    const [startsAt, setStartsAt] = useState<string>('');
    const [expectedEndAt, setExpectedEndAt] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [saving, setSaving] = useState<boolean>(false);
    const [confirming, setConfirming] = useState<Confirming>(null);

    const show = useCallback((current: MaintenanceWindow): void => {
        setMaintenance(current);
        setStartsAt(isoToLocalInput(current.startsAt));
        setExpectedEndAt(isoToLocalInput(current.expectedEndAt));
        setMessage(current.message);
    }, []);

    const load = useCallback(async (): Promise<void> => {
        try {
            show(await loadMaintenance());
            setLoadFailed(false);
        } catch {
            setLoadFailed(true);
        }
    }, [show]);

    useEffect((): (() => void) => {
        void load();
        const onChanged = (): void => { void load(); };
        window.addEventListener(MAINTENANCE_CHANGED, onChanged);

        return (): void => window.removeEventListener(MAINTENANCE_CHANGED, onChanged);
    }, [load]);

    const change = async (action: () => Promise<MaintenanceWindow>, done: string): Promise<void> => {
        setSaving(true);
        try {
            show(await action());
            notify(done, { type: 'success' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'The maintenance window could not be changed', { type: 'error' });
        } finally {
            setSaving(false);
            setConfirming(null);
        }
    };

    const schedule = (startNow: boolean): Promise<void> => change(
        (): Promise<MaintenanceWindow> => scheduleMaintenance({
            startsAt: startNow ? '' : localInputToIso(startsAt),
            expectedEndAt: localInputToIso(expectedEndAt),
            message,
        }),
        startNow ? 'Maintenance started' : 'Maintenance scheduled'
    );

    const banner = maintenance === null ? null : bannerFor(maintenance);

    return (
        <Box sx={ { p: 2, maxWidth: 760 } }>
            <Title title="Maintenance" />

            { maintenance === null && !loadFailed && <LinearProgress /> }
            { loadFailed && (
                <Alert severity="error" action={ <Button color="inherit" size="small" onClick={ (): void => { void load(); } }>{ 'Try again' }</Button> }>
                    { 'The maintenance window could not be loaded.' }
                </Alert>
            ) }

            { maintenance !== null && (
                <Stack spacing={ 3 }>
                    <Box sx={ { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' } }>
                        <Stack direction="row" alignItems="center" spacing={ 1.5 } sx={ { mb: 2 } }>
                            <Typography variant="h6" component="h2">{ 'Current window' }</Typography>
                            <Chip
                                label={ PHASE_LABELS[maintenance.phase] }
                                size="small"
                                color={ maintenance.phase === 'off' ? 'default' : maintenance.overrun ? 'error' : 'warning' }
                            />
                        </Stack>
                        { banner !== null && <Alert severity={ banner.severity } sx={ { mb: 2 } }>{ banner.text }</Alert> }
                        <Stack spacing={ 1 }>
                            <Detail label="Starts" value={ formatWindowTime(maintenance.startsAt) } />
                            <Detail label="Expected end" value={ formatWindowTime(maintenance.expectedEndAt) } />
                            <Detail label="Customer message" value={ maintenance.message } />
                        </Stack>
                    </Box>

                    { !canManage && (
                        <Alert severity="info">{ 'You can see the maintenance window but not change it.' }</Alert>
                    ) }

                    { canManage && (
                        <Box sx={ { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' } }>
                            <Typography variant="h6" component="h2" sx={ { mb: 1 } }>{ 'Plan a window' }</Typography>
                            <Typography variant="body2" color="text.secondary" sx={ { mb: 2 } }>
                                { 'Times are in your own time zone. Scoring, Email Finder and workflow runs pause shortly before the start. '
                                    + 'The customer portal and sign-in close at the start and stay closed until the window is ended, here or by the deploy.' }
                            </Typography>
                            <Stack spacing={ 2 }>
                                <Stack direction={ { xs: 'column', sm: 'row' } } spacing={ 2 }>
                                    <TextField
                                        type="datetime-local"
                                        label="Starts at"
                                        value={ startsAt }
                                        onChange={ (event): void => setStartsAt(event.target.value) }
                                        slotProps={ { inputLabel: { shrink: true } } }
                                        fullWidth
                                    />
                                    <TextField
                                        type="datetime-local"
                                        label="Expected end"
                                        helperText="Optional. Shown to customers; it does not end the window."
                                        value={ expectedEndAt }
                                        onChange={ (event): void => setExpectedEndAt(event.target.value) }
                                        slotProps={ { inputLabel: { shrink: true } } }
                                        fullWidth
                                    />
                                </Stack>
                                <TextField
                                    label="Message for customers"
                                    helperText={ `Optional. ${ message.length } of ${ MESSAGE_MAX_LENGTH } characters.` }
                                    value={ message }
                                    onChange={ (event): void => setMessage(event.target.value.slice(0, MESSAGE_MAX_LENGTH)) }
                                    multiline
                                    minRows={ 2 }
                                    fullWidth
                                />
                                <Stack direction="row" spacing={ 1.5 } useFlexGap flexWrap="wrap">
                                    <Button
                                        variant="contained"
                                        disabled={ saving || startsAt === '' }
                                        onClick={ (): void => { void schedule(false); } }
                                    >
                                        { 'Schedule' }
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        disabled={ saving }
                                        onClick={ (event): void => { blurTrigger(event); setConfirming('start'); } }
                                    >
                                        { 'Start now' }
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        disabled={ saving || maintenance.phase === 'off' }
                                        onClick={ (event): void => { blurTrigger(event); setConfirming('end'); } }
                                    >
                                        { 'End maintenance' }
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    ) }
                </Stack>
            ) }

            <Confirm
                isOpen={ confirming === 'start' }
                loading={ saving }
                title="Start maintenance now?"
                content="The customer portal and sign-in close straight away, and Operator runs pause until the window is ended."
                confirm="Start now"
                onConfirm={ (): void => { void schedule(true); } }
                onClose={ (): void => setConfirming(null) }
            />
            <Confirm
                isOpen={ confirming === 'end' }
                loading={ saving }
                title="End maintenance?"
                content="The customer portal reopens and paused runs resume on their next tick. Check the release first."
                confirm="End maintenance"
                onConfirm={ (): void => { void change(endMaintenance, 'Maintenance ended'); } }
                onClose={ (): void => setConfirming(null) }
            />
        </Box>
    );
};
